import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { defineMessages, useIntl } from 'react-intl';
import Item from './select/Item';
import Button from '../Button';
import classNames from 'classnames';

const messages = defineMessages({
  Search: 'Search',
  LoadingProgress: 'Loading...',
  ErrorLoadingData: 'Error loading data',
  NoItems: 'No items',
  Clear: 'Clear',
});

const handleStateChange = (state, changes) => {
  const { inputValue } = state;

  return { ...changes, inputValue };
};

const TextInputWithSuggestions = ({
  value = '',
  onChange,
  placeholder = undefined,
  suggestions = [],
  inputType = 'text',
  inputClass = '',
  canClear = false,
  disabled = false,
  ...rest
}) => {
  const intl = useIntl();
  return (
    <Downshift
      {...rest}
      inputValue={value}
      onInputValueChange={inputValue => onChange(inputValue)}
      itemToString={item => (item ? item.value : '')}
      stateReducer={handleStateChange}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        clearSelection,
        getToggleButtonProps,
        onClear,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
      }) => (
        <div>
          <div className="input-group">
            <input
              data-1p-ignore
              {...getInputProps({
                placeholder,
                type: inputType,
                className: inputClass,
                disabled,
              })}
            />
            {(!inputValue || !canClear) && (
              <span className="input-group-btn">
                <Button
                  btnClassName=""
                  id="dropdownButton"
                  disabled={disabled}
                  {...getToggleButtonProps()}
                >
                  <span className="caret" />
                </Button>
              </span>
            )}

            {!disabled && canClear && inputValue && (
              <span className="input-group-btn">
                <Button
                  btnClassName="clearButton"
                  disabled={disabled}
                  title={intl.formatMessage(messages.Clear)}
                  onClick={() => {
                    clearSelection(); // Call downshift method
                    if (typeof onClear === 'function') onClear(); // Call props method to propogate to upper component
                    onChange(null);
                  }}
                >
                  <span className="fa fa-times" />
                </Button>
              </span>
            )}
          </div>
          <div className={classNames({ open: isOpen }, { 'Select--relative': true })}>
            <ul
              {...getMenuProps()}
              className="Select--dropdown dropdown-menu"
              style={{ overflowX: 'auto' }}
            >
              {isOpen
                ? suggestions
                    .filter(item => !inputValue || item.value.includes(inputValue))
                    .map((item, index) => (
                      <Item
                        key={`${index}-${item.key}`}
                        item={item}
                        index={index}
                        isSelected={highlightedIndex === index}
                        isActive={selectedItem === item}
                        getItemProps={getItemProps}
                        searchValue={inputValue}
                        disabled={item.disabled}
                      />
                    ))
                : null}
            </ul>
          </div>
        </div>
      )}
    </Downshift>
  );
};
TextInputWithSuggestions.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, value: PropTypes.string, label: PropTypes.string }),
  ),
  inputType: PropTypes.string,
  inputClass: PropTypes.string,
  canClear: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default TextInputWithSuggestions;
